import "@/index.css";
import "@/styles/brand.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { nativeHome, onAppReady } from "@/lib/nativeBridge";
import { getAuth, lastArea } from "@/lib/api";

import MarketingHome from "@/pages/marketing/MarketingHome";
import SuperLogin from "@/pages/super/SuperLogin";
import SuperLayout from "@/pages/super/SuperLayout";
import SuperDashboard from "@/pages/super/SuperDashboard";
import SuperTenants from "@/pages/super/SuperTenants";
import SuperTenantDetail from "@/pages/super/SuperTenantDetail";
import SuperPlans from "@/pages/super/SuperPlans";
import SuperMobileApps from "@/pages/super/SuperMobileApps";
import SuperStorage from "@/pages/super/SuperStorage";
import SuperSupport from "@/pages/super/SuperSupport";

import TenantRoot from "@/pages/tenant/TenantRoot";
import TenantShell from "@/pages/tenant/TenantShell";
import TenantHome from "@/pages/tenant/TenantHome";
import TenantCategory from "@/pages/tenant/TenantCategory";
import TenantService from "@/pages/tenant/TenantService";
import TenantBook from "@/pages/tenant/TenantBook";
import TenantEnquiry from "@/pages/tenant/TenantEnquiry";
import TenantBookings from "@/pages/tenant/TenantBookings";
import TenantAccount from "@/pages/tenant/TenantAccount";
import TenantSecurity from "@/pages/tenant/TenantSecurity";
import TenantLegal from "@/pages/tenant/TenantLegal";
import TenantLogin from "@/pages/tenant/TenantLogin";
import TenantSearch from "@/pages/tenant/TenantSearch";

import TAdminLogin from "@/pages/tadmin/TAdminLogin";
import TAdminLayout from "@/pages/tadmin/TAdminLayout";
import TAdminDashboard from "@/pages/tadmin/TAdminDashboard";
import TAdminServices from "@/pages/tadmin/TAdminServices";
import TAdminCategories from "@/pages/tadmin/TAdminCategories";
import TAdminSellers from "@/pages/tadmin/TAdminSellers";
import TAdminBookings from "@/pages/tadmin/TAdminBookings";
import TAdminEnquiries from "@/pages/tadmin/TAdminEnquiries";
import TAdminBranding from "@/pages/tadmin/TAdminBranding";
import TAdminProfile from "@/pages/tadmin/TAdminProfile";

import SellerLogin from "@/pages/seller/SellerLogin";
import SellerLayout from "@/pages/seller/SellerLayout";
import SellerDashboard from "@/pages/seller/SellerDashboard";
import SellerBookings from "@/pages/seller/SellerBookings";

/** An unknown page under a tenant goes to that tenant's home — never to another tenant or the platform site. */
function TenantNotFound() {
  const { slug } = useParams();
  return <Navigate to={`/t/${slug}`} replace />;
}

/**
 * Where a native app may be. An app is built for one tenant, and nobody can
 * type an address in it, so the app itself has to take people to the right
 * place and keep them out of the wrong ones:
 *
 *   - that tenant's pages: always (each area still checks its own sign-in);
 *   - the Super Admin console: only while a Super Admin is signed in. Signing in
 *     with a Super Admin's number on the app's sign-in screen opens it;
 *   - anything else (another tenant, the platform's marketing site): never. The
 *     app goes back to its own start page.
 *
 * When the app is opened again it returns to the area that was signed in to
 * last (the admin console, say), not to the customer home.
 */
function NativeTenantGuard() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const home = nativeHome();
    if (!home) return;
    const path = location.pathname;
    const tenantBase = home.split("/").slice(0, 3).join("/");
    const inTenant = path === tenantBase || path.startsWith(`${tenantBase}/`);
    const inSuper = (path === "/super" || path.startsWith("/super/")) && path !== "/super/login" && !!getAuth("super");
    if (!inTenant && !inSuper) { navigate(home, { replace: true }); return; }

    // Once per launch: reopen where the person was signed in last.
    if (!sessionStorage.getItem("las_native_resumed")) {
      sessionStorage.setItem("las_native_resumed", "1");
      const area = lastArea();
      const allowed = area && (area === "/super" || area === tenantBase || area.startsWith(`${tenantBase}/`));
      if (path === home && allowed && area !== home) navigate(area, { replace: true });
    }
  }, [location.pathname, navigate]);
  return null;
}

function App() {
  useEffect(() => { onAppReady(); }, []);

  return (
    <>
      <BrowserRouter>
        <NativeTenantGuard />
        <Routes>
          {/* SaaS marketing */}
          <Route path="/" element={<MarketingHome />} />

          {/* Super Admin */}
          <Route path="/super/login" element={<SuperLogin />} />
          <Route path="/login" element={<SuperLogin general />} />
          <Route path="/super" element={<SuperLayout />}>
            <Route index element={<SuperDashboard />} />
            <Route path="tenants" element={<SuperTenants />} />
            <Route path="tenants/:tenantId" element={<SuperTenantDetail />} />
            <Route path="plans" element={<SuperPlans />} />
            <Route path="mobile-apps" element={<SuperMobileApps />} />
            <Route path="storage" element={<SuperStorage />} />
            <Route path="support" element={<SuperSupport />} />
          </Route>

          {/* Tenant Admin */}
          <Route path="/t/:slug/admin/login" element={<TAdminLogin />} />
          <Route path="/t/:slug/admin" element={<TAdminLayout />}>
            <Route index element={<TAdminDashboard />} />
            <Route path="services" element={<TAdminServices />} />
            <Route path="categories" element={<TAdminCategories />} />
            <Route path="sellers" element={<TAdminSellers />} />
            <Route path="bookings" element={<TAdminBookings />} />
            <Route path="enquiries" element={<TAdminEnquiries />} />
            <Route path="branding" element={<TAdminBranding />} />
            <Route path="profile" element={<TAdminProfile />} />
          </Route>

          {/* Seller */}
          <Route path="/t/:slug/seller/login" element={<SellerLogin />} />
          <Route path="/t/:slug/seller" element={<SellerLayout />}>
            <Route index element={<SellerDashboard />} />
            <Route path="bookings" element={<SellerBookings />} />
          </Route>

          {/* Customer app — everything here is loaded for the tenant in the URL */}
          <Route path="/t/:slug" element={<TenantRoot />}>
            <Route path="login" element={<TenantLogin />} />
            <Route path="privacy-policy" element={<TenantLegal doc="privacy-policy" />} />
            <Route path="terms" element={<TenantLegal doc="terms" />} />
            <Route path="account-deletion" element={<TenantLegal doc="account-deletion" />} />
            <Route element={<TenantShell />}>
              <Route index element={<TenantHome />} />
              <Route path="search" element={<TenantSearch />} />
              <Route path="category/:catId" element={<TenantCategory />} />
              <Route path="service/:serviceId" element={<TenantService />} />
              <Route path="book/:serviceId" element={<TenantBook />} />
              <Route path="enquiry" element={<TenantEnquiry />} />
              <Route path="bookings" element={<TenantBookings />} />
              <Route path="account" element={<TenantAccount />} />
              <Route path="account/security" element={<TenantSecurity />} />
            </Route>
            <Route path="*" element={<TenantNotFound />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </>
  );
}

export default App;
